import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { streamLLM, api } from "@/lib/api";
import StreamBox, { PlatformPicker, PrimaryButton, GhostButton, TextArea } from "@/components/StreamBox";
import VoiceButton from "@/components/VoiceButton";
import { useSubscription } from "@/hooks/useSubscription";
import { toast } from "sonner";
import { Copy, Save } from "lucide-react";

export default function Remix() {
  const navigate = useNavigate();
  const { refresh: refreshSub } = useSubscription();
  const [content, setContent] = useState("");
  const [platform, setPlatform] = useState("twitter");
  const [tone, setTone] = useState("bold");
  const [output, setOutput] = useState("");
  const [streaming, setStreaming] = useState(false);

  const handleError = (e) => {
    setStreaming(false);
    if (e === "QUOTA_EXCEEDED") {
      toast.error("Free daily limit hit. Upgrade to Pro for unlimited.", {
        action: { label: "Upgrade", onClick: () => navigate("/app/pricing") },
      });
    } else {
      toast.error(e);
    }
  };

  const run = async () => {
    if (!content.trim() || content.length < 5) return toast.error("Paste content first (min 5 chars).");
    setOutput("");
    setStreaming(true);
    await streamLLM("/generate/remix", { content, platform, tone }, {
      onDelta: (d) => setOutput((p) => p + d),
      onDone: () => { setStreaming(false); refreshSub(); },
      onError: handleError,
    });
  };

  const save = async () => {
    if (!output.trim()) return;
    try {
      await api.post("/library", { type: "remix", platform, input: content, output });
      toast.success("Saved to Vault.");
    } catch (e) { toast.error("Save failed."); }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(output);
    toast.success("Copied.");
  };

  return (
    <div className="p-6 md:p-10 lg:p-12 space-y-8">
      <header className="border-b border-white/10 pb-6">
        <div className="label-eyebrow">// module 02</div>
        <h1 className="heading-display text-4xl sm:text-5xl mt-2">REMIX <span className="text-signal-yellow">LAB</span></h1>
        <p className="font-mono text-xs text-zinc-500 mt-3">// paste anything. get a viral rewrite tuned for the platform.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-5">
          <TextArea
            data-testid="remix-content"
            label="Source content"
            placeholder="Paste your existing post, tweet, script, or paragraph here..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
          <VoiceButton testid="remix-voice" label="Voice input" onText={(t) => setContent((p) => (p ? p + "\n" + t : t))} />
          <div className="space-y-2">
            <span className="label-eyebrow block">Target platform</span>
            <PlatformPicker value={platform} onChange={setPlatform} name="remix-platform" />
          </div>
          <label className="block space-y-2">
            <span className="label-eyebrow block">Tone</span>
            <select
              data-testid="remix-tone"
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              className="w-full bg-[#141414] border border-white/10 text-white p-3 font-mono text-sm focus:border-signal-yellow focus:outline-none"
            >
              <option value="bold">Bold</option>
              <option value="curious">Curious</option>
              <option value="contrarian">Contrarian</option>
              <option value="story">Story-driven</option>
              <option value="emotional">Emotional</option>
              <option value="witty">Witty</option>
            </select>
          </label>
          <PrimaryButton data-testid="remix-generate" onClick={run} disabled={streaming} className="w-full">
            {streaming ? "Remixing..." : "Remix it →"}
          </PrimaryButton>
        </div>

        <div className="space-y-3">
          <StreamBox text={output} streaming={streaming} label="Remix Output" />
          {output && !streaming && (
            <div className="flex gap-2">
              <GhostButton data-testid="remix-copy" onClick={copy}><Copy size={12} className="inline mr-2" />Copy</GhostButton>
              <GhostButton data-testid="remix-save" onClick={save}><Save size={12} className="inline mr-2" />Save to Vault</GhostButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
